import { FC, useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Tab, Tabs, Alert } from "react-bootstrap";
import { useSnapshot } from "valtio";
import { ConversionRulesService } from "@services/conversionRules/conversionRulesService";
import { conversionRuleStore } from "./conversionRuleStore";
import { ConversionRuleBuilder, type RuleGroup } from "./ConversionRuleBuilder";
import { compileRuleGroup } from "./compileRuleGroup";
import { tryParseRuleText } from "./parseRuleText";
import { findIncompleteRuleIds, findUnlistedDimensionRuleIds } from "./validateRuleGroup";

interface Props {
  show: boolean;
  value: string;
  organizationId: number;
  onClose: () => void;
  onSave: (ruleText: string) => void;
}

const EMPTY_GROUP: RuleGroup = { id: "root", combinator: "AND", rules: [] };

const ConversionRuleModal: FC<Props> = ({ show, value, organizationId, onClose, onSave }) => {
  const [group, setGroup] = useState<RuleGroup>(EMPTY_GROUP);
  const [ruleText, setRuleText] = useState<string>("");
  const [jsonDraft, setJsonDraft] = useState<string>("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  // The saved rule could not be read into the builder. Editing it there would
  // recompile from an empty group and silently replace the rule (MPN-1529), so
  // the builder is blocked and the JSON tab is the only way to change it.
  const [unparsed, setUnparsed] = useState(false);
  const [activeTab, setActiveTab] = useState("builder");
  const { entities, isLoadingEntities, loadError } = useSnapshot(conversionRuleStore);

  useEffect(() => {
    if (!show) return;
    const parsed = tryParseRuleText(value);
    setGroup(parsed ?? EMPTY_GROUP);
    setUnparsed(parsed === null);
    setRuleText(value);
    setJsonDraft(JSON.stringify({ rule_text: value }, null, 2));
    setJsonError(null);
    setActiveTab(parsed === null ? "json" : "builder");

    new ConversionRulesService()
      .loadSchema(organizationId)
      .catch((err) => console.error("ConversionRuleModal: loadSchema failed", err));
  }, [show, organizationId, value]);

  const handleGroupChange = (next: RuleGroup) => {
    // Refuse to let builder edits define the rule while the saved text is
    // unreadable — the group is empty, so compiling it would blank the rule.
    if (unparsed) return;
    setGroup(next);
    const compiled = compileRuleGroup(next);
    setRuleText(compiled);
    setJsonDraft(JSON.stringify({ rule_text: compiled }, null, 2));
    setJsonError(null);
  };

  const handleJsonChange = (raw: string) => {
    setJsonDraft(raw);
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.rule_text !== "string") {
        setJsonError('Expected { "rule_text": "..." }');
        return;
      }
      setRuleText(parsed.rule_text);
      const reparsed = tryParseRuleText(parsed.rule_text);
      setGroup(reparsed ?? EMPTY_GROUP);
      setUnparsed(reparsed === null);
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e?.message ?? "Invalid JSON");
    }
  };

  const incompleteIds = useMemo(() => findIncompleteRuleIds(group), [group]);
  const unlistedIds = useMemo(
    () => findUnlistedDimensionRuleIds(group, entities),
    [group, entities],
  );
  const invalidRuleIds = useMemo(
    () => new Set([...incompleteIds, ...unlistedIds]),
    [incompleteIds, unlistedIds],
  );
  const hasIncomplete = incompleteIds.length > 0;
  const hasUnlisted = unlistedIds.length > 0;

  const handleSave = () => {
    if (jsonError) return;
    if (hasIncomplete || hasUnlisted) return;
    onSave(ruleText);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      backdrop="static"
      container={document.body}
      className="tier-2-modal"
      backdropClassName="tier-2-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>Conversion Rule</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: 500 }}>
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "builder")}
          className="mb-3"
        >
          <Tab eventKey="builder" title="Visual Builder">
            <div style={{ maxHeight: "50vh", overflowY: "auto", padding: "16px 0" }}>
              {loadError && (
                <Alert variant="danger" className="py-2 px-3 mb-2">
                  {loadError}
                </Alert>
              )}
              {!loadError && !isLoadingEntities && entities.length === 0 && (
                <Alert variant="info" className="py-2 px-3 mb-2">
                  No queryable fields are configured for this organization yet — set up its
                  ingest configuration first.
                </Alert>
              )}
              {unparsed && (
                <Alert variant="danger" className="py-2 px-3 mb-2">
                  This rule could not be read into the visual builder, so editing it
                  here would replace it. Use the JSON View tab to change it, or
                  Cancel to leave it as it is.
                </Alert>
              )}
              {hasIncomplete && (
                <Alert variant="warning" className="py-2 px-3 mb-2">
                  {incompleteIds.length} incomplete condition
                  {incompleteIds.length === 1 ? "" : "s"} — pick an entity, field, operator, and
                  value (where applicable) before saving.
                </Alert>
              )}
              {hasUnlisted && (
                <Alert variant="danger" className="py-2 px-3 mb-2">
                  {unlistedIds.length} condition{unlistedIds.length === 1 ? " uses" : "s use"} a
                  dimension this client's field map does not write — pick a listed value or
                  "Any" before saving.
                </Alert>
              )}
              {!unparsed && (
                <ConversionRuleBuilder
                  rules={group}
                  invalidRuleIds={invalidRuleIds}
                  onChange={handleGroupChange}
                />
              )}
            </div>
          </Tab>
          <Tab eventKey="json" title="JSON View">
            <div style={{ padding: "16px 0" }}>
              {jsonError && (
                <Alert variant="danger" className="py-2 px-3 mb-2">
                  {jsonError}
                </Alert>
              )}
              <Form.Control
                as="textarea"
                rows={20}
                value={jsonDraft}
                onChange={(e) => handleJsonChange(e.target.value)}
                style={{ fontFamily: "monospace", fontSize: 12 }}
                spellCheck={false}
              />
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!!jsonError || hasIncomplete || hasUnlisted}
          title={hasIncomplete ? "Finish or remove incomplete conditions first" : undefined}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConversionRuleModal;
